import { z } from 'zod';

// ====================================================================
// Skema untuk Relasi
// ====================================================================

/**
 * Skema untuk relasi Company.
 * Didasarkan secara akurat pada contoh respons API.
 */
export const CompanyV4Schema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    address: z.string().nullable(),
    email: z.string().email().nullable(),
    phone: z.string().nullable(),
    logo: z.string().nullable(),
    cover_image: z.string().nullable(),
    // Field URL yang diisi user bisa tanpa protokol (mis. "gotrasoft.com"),
    // jadi divalidasi sebagai string biasa agar tidak memecahkan seluruh halaman.
    url: z.string().nullable(),
    color: z.string().nullable(),
    additional: z.any().nullable(), // Tipe 'any' karena bisa beragam
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    deleted_at: z.string().datetime().nullable(),
    laravel_through_key: z.string().uuid().optional(), // Opsional karena hanya ada di konteks relasi tertentu
}).nullable(); // Company bisa null

/**
 * Skema untuk relasi Group.
 * Didasarkan secara akurat pada contoh respons API.
 */
export const GroupV4Schema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    channex_id: z.string().uuid().nullable(),
    description: z.string().nullable(),
    company_id: z.string().uuid(),
    is_editable: z.number().or(z.boolean()),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    pivot: z.object({
        user_id: z.string().uuid(),
        group_id: z.string().uuid(),
    }).optional(),
});

/**
 * Skema untuk relasi Property.
 * Didasarkan secara akurat pada contoh respons API.
 */
export const PropertyV4Schema = z.object({
    id: z.string().uuid(),
    channex_id: z.string().uuid().nullable(),
    name: z.string(),
    accommodation_type: z.coerce.number(),
    preferred_currency: z.string(),
    time_zone: z.string(),
    primary_email: z.string().email().nullable(),
    cc_emails: z.array(z.string().email()).nullable(),
    accounting_email: z.string().email().nullable(),
    mobile_number_country_code: z.string().nullable(),
    mobile_number: z.string().nullable(),
    enable_cvv: z.number().or(z.boolean()), // Bisa 0/1 atau true/false
    primary_phone_country_code: z.string().nullable(),
    primary_phone: z.string().nullable(),
    website: z.string().nullable(),
    country: z.string().nullable(),
    state: z.string().nullable(),
    city: z.string().nullable(),
    street_address: z.string().nullable(),
    postal_code: z.string().nullable(),
    prefix_booking_number: z.string().nullable(),
    hotel_tax_number: z.string().nullable(),
    latitude: z.coerce.number().nullable(),
    longitude: z.coerce.number().nullable(),
    description: z.string().nullable(),
    // Link sosial media & website diisi user, bisa tanpa protokol → string biasa.
    facebook_link: z.string().nullable(),
    instagram_link: z.string().nullable(),
    youtube_link: z.string().nullable(),
    tiktok_link: z.string().nullable(),
    logo: z.string().nullable(),
    cover_image: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    is_full_sync: z.boolean(),
    property_type: z.string().nullable(),
    // settings backend punya key tambahan (min_price, cut_off_days, dst) — passthrough
    // agar penambahan field baru di V4 tidak memecahkan validasi.
    settings: z.object({
        allow_availability_autoupdate_on_confirmation: z.boolean(),
        allow_availability_autoupdate_on_modification: z.boolean(),
        allow_availability_autoupdate_on_cancellation: z.boolean(),
    }).passthrough().nullable(),
    logo_url: z.string().nullable(),
    pivot: z.object({
        user_id: z.string().uuid(),
        property_id: z.string().uuid(),
        is_active: z.number().or(z.boolean()),
        created_at: z.string().datetime(),
        updated_at: z.string().datetime(),
    }).optional(),
});

// ====================================================================
// Skema Utama untuk User
// ====================================================================

/**
 * Skema Zod untuk satu objek Pengguna (Versi 4).
 * Ini digunakan untuk memvalidasi data pengguna individu yang diterima dari API.
 * Relasi didefinisikan sebagai opsional karena tidak selalu di-include.
 */
export const UserV4Schema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().email(),
    phone: z.string().nullable(),
    email_verified_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    channex_user_api_key: z.string().nullable(),
    username: z.string().nullable(),
    roles: z.array(z.object({
        id: z.number().int(),
        name: z.string(),
        pivot: z.object({
            model_type: z.string(),
            model_id: z.string().uuid(),
            role_id: z.number().int(),
        }),
    })).optional(), // Relasi roles bersifat opsional

    // Relasi (didefinisikan sebagai opsional dan bisa berupa array kosong)
    company: CompanyV4Schema.optional(),
    groups: z.array(GroupV4Schema).optional(),
    properties: z.array(PropertyV4Schema).optional(),
});


// ====================================================================
// Tipe TypeScript yang Diekspor
// ====================================================================

/**
 * Tipe TypeScript untuk objek Company, diturunkan dari skema Zod.
 */
export type CompanyV4 = z.infer<typeof CompanyV4Schema>;

/**
 * Tipe TypeScript untuk objek Group, diturunkan dari skema Zod.
 */
export type GroupV4 = z.infer<typeof GroupV4Schema>;

/**
 * Tipe TypeScript untuk objek Property, diturunkan dari skema Zod.
 */
export type PropertyV4 = z.infer<typeof PropertyV4Schema>;

/**
 * Tipe TypeScript untuk satu objek Pengguna, diturunkan dari skema Zod.
 * Tipe ini akan menjadi tipe data kanonis untuk pengguna di seluruh fitur V4.
 */
export type UserV4 = z.infer<typeof UserV4Schema>;